import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Breadcrumbs,
  Link
} from '@mui/material';
import {
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import SecretarySidebar from '../../components/SecretarySidebar';

// Types
interface Level {
  id: number;
  name: string;
}

interface Class {
  id: number;
  name: string;
  level_id: number;
}

interface SchoolYear {
  id: number;
  year: string;
  name?: string;
}

interface Trimester {
  id: number;
  name: string;
  school_year: string;
}

interface SubjectGrade {
  subject_name: string;
  average: number;
  rank: number | null;
  coefficient: number;
  weighted_average: number;
  category: string;
}

interface StudentData {
  studentId: string;
  matricule: string;
  last_name: string;
  first_name: string;
  average: number;
  rank: number;
  subjects: SubjectGrade[];
  level_name?: string;
  class_name?: string;
}

const ExcelFiles: React.FC = () => {
  const [searchParams] = useSearchParams();
  const exportType = searchParams.get('type') as 'moyennes' | 'recap' | null;
  
  // States
  const [levels, setLevels] = useState<Level[]>([]);
  const [classes, setClasses] = useState<Class[]>([]);
  const [students, setStudents] = useState<StudentData[]>([]);
  const [schoolYears, setSchoolYears] = useState<SchoolYear[]>([]);
  const [trimesters, setTrimesters] = useState<Trimester[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<Level | null>(null);
  const [selectedClass, setSelectedClass] = useState<Class | null>(null);
  const [selectedSchoolYear, setSelectedSchoolYear] = useState<SchoolYear | null>(null);
  const [selectedTrimester, setSelectedTrimester] = useState<Trimester | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingSchoolYears, setLoadingSchoolYears] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentView, setCurrentView] = useState<'levels' | 'classes' | 'students' | 'allStudents'>('levels');

  // Load data on component mount
  useEffect(() => {
    fetchLevels();
    fetchSchoolYears();
    fetchTrimesters();
  }, []);

  // Fetch levels
  const fetchLevels = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trayebernard-college.com/api/levels', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLevels(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des niveaux:', error);
      setError('Erreur lors du chargement des niveaux');
    } finally {
      setLoading(false);
    }
  };

  // Fetch classes for a level
  const fetchClasses = async (level: Level) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://trayebernard-college.com/api/classes?level_id=${level.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClasses(response.data);
      setSelectedLevel(level);
      setCurrentView('classes');
    } catch (error) {
      console.error('Erreur lors du chargement des classes:', error);
      setError('Erreur lors du chargement des classes');
    } finally {
      setLoading(false);
    }
  };

  // Fetch school years
  const fetchSchoolYears = async () => {
    try {
      setLoadingSchoolYears(true);
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trayebernard-college.com/api/school-years', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSchoolYears(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des années scolaires:', error);
      const defaultYears = [
        { id: 1, year: '2023-2024', name: '2023-2024' },
        { id: 2, year: '2024-2025', name: '2024-2025' }
      ];
      setSchoolYears(defaultYears);
      setError('Utilisation des années scolaires par défaut');
    } finally {
      setLoadingSchoolYears(false);
    }
  };

  // Fetch trimesters
  const fetchTrimesters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://trayebernard-college.com/api/trimesters', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTrimesters(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des trimestres:', error);
      const defaultTrimesters = [
        { id: 1, name: '1 ème trimestre', school_year: '2024-2025' },
        { id: 2, name: '2 ème trimestre', school_year: '2024-2025' },
        { id: 3, name: '3 ème trimestre', school_year: '2024-2025' }
      ];
      setTrimesters(defaultTrimesters);
    }
  };

  // Fetch all students data for the entire establishment
  const fetchAllStudentsData = async (schoolYear: SchoolYear, trimester: Trimester) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      console.log('=== DEBUG fetchAllStudentsData ===');
      console.log('schoolYear:', schoolYear);
      console.log('trimester:', trimester);
      
      const allStudentsData: StudentData[] = [];
      
      for (const level of levels) {
        try {
          const classesResponse = await axios.get(`https://trayebernard-college.com/api/classes?level_id=${level.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          const levelClasses = classesResponse.data;
          
          for (const classItem of levelClasses) {
            try {
              const params: any = {
                school_year: schoolYear.name || schoolYear.year
              };
              
              if (exportType === 'moyennes') {
                params.trimester_id = trimester.id;
                params.trimester_name = trimester.name;
              } else {
                params.trimester = trimester.id;
              }
              
              const endpoint = exportType === 'moyennes' 
                ? `https://trayebernard-college.com/api/classes/${classItem.id}/students-detailed-grades`
                : `https://trayebernard-college.com/api/classes/${classItem.id}/students-grades`;
              
              const response = await axios.get(endpoint, {
                headers: { Authorization: `Bearer ${token}` },
                params: params
              });
              
              let studentsData = Array.isArray(response.data) ? response.data : [];
              
              studentsData = studentsData.map(student => ({
                ...student,
                level_name: level.name,
                class_name: classItem.name
              } as any));
              
              allStudentsData.push(...studentsData);
            } catch (classError) {
              console.error(`Erreur pour la classe ${classItem.name}:`, classError);
            }
          }
        } catch (levelError) {
          console.error(`Erreur pour le niveau ${level.name}:`, levelError);
        }
      }
      
      console.log('Total élèves récupérés:', allStudentsData.length);
      console.log('Premier élève:', allStudentsData[0]);
      
      setStudents(allStudentsData);
      setSelectedSchoolYear(schoolYear);
      setSelectedTrimester(trimester);
      setCurrentView('allStudents');
      
      if (allStudentsData.length === 0) {
        setError(`Aucun élève trouvé pour l'année scolaire ${schoolYear.name || schoolYear.year} et le ${trimester.name}`);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement de tous les élèves:', error);
      setError(`Erreur lors du chargement des données pour tout l'établissement - ${schoolYear.name || schoolYear.year} - ${trimester.name}`);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch students data for a specific class
  const fetchStudentsData = async (classItem: Class, schoolYear: SchoolYear, trimester: Trimester) => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('token');
      console.log('=== DEBUG fetchStudentsData ===');
      console.log('classItem:', classItem);
      console.log('schoolYear:', schoolYear);
      console.log('trimester:', trimester);
      console.log('exportType:', exportType);
      
      const params: any = {
        school_year: schoolYear.name || schoolYear.year
      };
      
      if (exportType === 'moyennes') {
        params.trimester_id = trimester.id;
        params.trimester_name = trimester.name;
      } else {
        params.trimester = trimester.id;
      }
      
      const endpoint = exportType === 'moyennes' 
        ? `https://trayebernard-college.com/api/classes/${classItem.id}/students-detailed-grades`
        : `https://trayebernard-college.com/api/classes/${classItem.id}/students-grades`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        params: params
      });
      
      let studentsData = Array.isArray(response.data) ? response.data : [];
      
      console.log('Données récupérées:', studentsData);
      console.log('Nombre d\'élèves:', studentsData.length);
      
      setStudents(studentsData);
      setSelectedClass(classItem);
      setSelectedSchoolYear(schoolYear);
      setSelectedTrimester(trimester);
      setCurrentView('students');
      
      if (studentsData.length === 0) {
        setError(`Aucun élève trouvé pour la classe ${classItem.name} - ${schoolYear.name || schoolYear.year} - ${trimester.name}`);
      }
    } catch (error: any) {
      console.error('Erreur lors du chargement des données:', error);
      setError(`Erreur lors du chargement des données pour ${classItem.name} - ${schoolYear.name || schoolYear.year} - ${trimester.name}`);
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  // Download Excel file
  const downloadExcel = () => {
    const studentsArray = Array.isArray(students) ? students : [];
    
    if (studentsArray.length === 0) {
      setError('Aucune donnée à exporter');
      return;
    }
    
    try {
      const wb = XLSX.utils.book_new();
      
      // Récupérer toutes les matières uniques
      const allSubjects = new Set<string>();
      studentsArray.forEach(student => {
        if (student.subjects && student.subjects.length > 0) {
          student.subjects.forEach(subject => {
            allSubjects.add(subject.subject_name);
          });
        }
      });
      const subjectsArray = Array.from(allSubjects).sort();
      
      // Créer les en-têtes dans le bon ordre
      const headers = [
        'Matricule',
        'Nom',
        'Prénom',
        'Nom de l\'établissement',
        'Niveau',
        ...subjectsArray,
        'Moyenne Trimestrielle',
        'Rang'
      ];
      
      // Trier les élèves par niveau dans l'ordre exact : 6ème, 5ème, 4ème, 3ème, 2nde, 1ère, Terminale
      const sortedStudents = studentsArray.sort((a, b) => {
        // Normaliser les noms de niveaux pour la comparaison
        const normalizeLevel = (level: string) => {
          if (!level) return '';
          return level.toLowerCase().trim().replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a');
        };
        
        const levelA = normalizeLevel((a as any).level_name || '');
        const levelB = normalizeLevel((b as any).level_name || '');
        
        // Trouver l'index dans l'ordre des niveaux
        const getLevelIndex = (level: string) => {
          if (level.includes('6') || level.includes('six')) return 0;
          if (level.includes('5') || level.includes('cinq')) return 1;
          if (level.includes('4') || level.includes('quatre')) return 2;
          if (level.includes('3') || level.includes('trois')) return 3;
          if (level.includes('2nde') || level.includes('seconde')) return 4;
          if (level.includes('1') || level.includes('premiere') || level.includes('première')) return 5;
          if (level.includes('terminale')) return 6;
          return 999; // Niveau non reconnu
        };
        
        const indexA = getLevelIndex(levelA);
        const indexB = getLevelIndex(levelB);
        
        // D'abord trier par niveau (6ème en premier, Terminale en dernier)
        if (indexA !== indexB) return indexA - indexB;
        
        // Ensuite trier par classe dans le même niveau
        if ((a as any).class_name !== (b as any).class_name) return ((a as any).class_name || '').localeCompare((b as any).class_name || '');
        
        // Enfin trier par nom de famille puis prénom
        const lastNameA = (a.last_name || '').trim().toLowerCase();
        const lastNameB = (b.last_name || '').trim().toLowerCase();
        const firstNameA = (a.first_name || '').trim().toLowerCase();
        const firstNameB = (b.first_name || '').trim().toLowerCase();
        
        if (lastNameA < lastNameB) return -1;
        if (lastNameA > lastNameB) return 1;
        if (firstNameA < firstNameB) return -1;
        if (firstNameA > firstNameB) return 1;
        return 0;
      });
      
      // Créer les données dans le bon ordre
      const data = sortedStudents.map(student => {
        const row: any = {
          'Matricule': student.matricule,
          'Nom': student.last_name,
          'Prénom': student.first_name,
          'Nom de l\'établissement': 'GROUPE SCOLAIRE TRAYE BERNARD',
          'Niveau': (student as any).level_name
        };
        
        // Ajouter les moyennes des matières
        subjectsArray.forEach(subjectName => {
          const subject = student.subjects?.find(s => s.subject_name === subjectName);
          if (subject) {
            if (subject.rank === null || subject.rank === undefined) {
              row[subjectName] = '(21)'; // Élève non classé
            } else {
              row[subjectName] = Number(subject.average || 0).toFixed(2);
            }
          } else {
            row[subjectName] = '(22)'; // Matière pas dans l'emploi du temps
          }
        });
        
        row['Moyenne Trimestrielle'] = Number(student.average || 0).toFixed(2);
        row['Rang'] = student.rank;
        
        return row;
      });
      
      const ws = XLSX.utils.json_to_sheet(data);
      
      // Définir la largeur des colonnes
      const colWidths = headers.map(header => {
        if (header === 'Nom de l\'établissement') return { wch: 30 };
        if (header === 'Moyenne Trimestrielle') return { wch: 18 };
        if (subjectsArray.includes(header)) return { wch: 12 };
        return { wch: 15 };
      });
      ws['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(wb, ws, 'Rapport Complet');
      
      const fileName = `rapport_complet_etablissement_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      setError(null);
    } catch (error) {
      console.error('Erreur lors de la génération du fichier Excel:', error);
      setError('Erreur lors de la génération du fichier Excel');
    }
  };

  // Go back function
  const goBack = () => {
    if (currentView === 'students' || currentView === 'allStudents') {
      setCurrentView('levels');
      setStudents([]);
      setClasses([]);
      setSelectedLevel(null);
      setSelectedClass(null);
    } else if (currentView === 'classes') {
      setCurrentView('levels');
      setClasses([]);
      setSelectedLevel(null);
    }
  };

  // Reset all
  const resetAll = () => {
    setSelectedLevel(null);
    setSelectedClass(null);
    setSelectedSchoolYear(null);
    setSelectedTrimester(null);
    setStudents([]);
    setClasses([]);
    setCurrentView('levels');
    setError(null);
  };

  // Render levels
  const renderLevels = () => (
    <Box>
      {/* Bouton pour exporter tout l'établissement */}
      {exportType === 'moyennes' && selectedSchoolYear && selectedTrimester && (
        <Paper sx={{ 
          p: 3, 
          mb: 4,
          borderRadius: 2,
          backgroundColor: '#e8f5e8',
          border: '2px solid #4caf50',
          textAlign: 'center'
        }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#2e7d32', mb: 2 }}>
            📊 Export pour tout l'établissement
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            Récupérer tous les élèves de tous les niveaux (6ème à Terminale) avec leurs moyennes détaillées
          </Typography>
          <Button
            variant="contained"
            onClick={() => fetchAllStudentsData(selectedSchoolYear, selectedTrimester)}
            sx={{
              backgroundColor: '#4caf50',
              color: 'white',
              fontWeight: 700,
              borderRadius: 2,
              px: 4,
              py: 2,
              fontSize: '1.1rem',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
              '&:hover': {
                backgroundColor: '#388e3c',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(76, 175, 80, 0.4)'
              }
            }}
          >
            🏫 Exporter pour tout l'établissement
          </Button>
        </Paper>
      )}

      {/* Filtres */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Filtres
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Année scolaire
            </Typography>
            {loadingSchoolYears ? (
              <CircularProgress size={20} />
            ) : (
              <select
                value={selectedSchoolYear?.id || ''}
                onChange={(e) => {
                  const year = schoolYears.find(y => y.id === parseInt(e.target.value));
                  setSelectedSchoolYear(year || null);
                }}
                style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="">Sélectionner une année</option>
                {schoolYears.map(year => (
                  <option key={year.id} value={year.id}>
                    {year.name || year.year}
                  </option>
                ))}
              </select>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Trimestre
            </Typography>
            <select
              value={selectedTrimester?.id || ''}
              onChange={(e) => {
                const trimester = trimesters.find(t => t.id === parseInt(e.target.value));
                setSelectedTrimester(trimester || null);
              }}
              style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
            >
              <option value="">Sélectionner un trimestre</option>
              {trimesters.map(trimester => (
                <option key={trimester.id} value={trimester.id}>
                  {trimester.name}
                </option>
              ))}
            </select>
          </Grid>
        </Grid>
      </Paper>

      {/* Liste des niveaux */}
      <Grid container spacing={4}>
        {levels.map((level, index) => {
          const colors = [
            '#1976d2', // Bleu
            '#388e3c', // Vert
            '#f57c00', // Orange
            '#7b1fa2', // Violet
            '#c2185b', // Rose
            '#0288d1', // Bleu clair
            '#388e3c'  // Vert foncé
          ];
          const color = colors[index % colors.length];
          
          return (
            <Grid item xs={12} sm={6} md={4} key={level.id}>
              <Card 
                sx={{ 
                  cursor: 'pointer', 
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                  },
                  border: `2px solid ${color}`,
                  borderRadius: 2
                }}
                onClick={() => fetchClasses(level)}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <SchoolIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: color, 
                      mb: 2 
                    }} 
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: color }}>
                    {level.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                    Cliquer pour voir les classes
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // Render classes
  const renderClasses = () => (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link 
          color="inherit" 
          href="#" 
          onClick={(e) => {
            e.preventDefault();
            goBack();
          }}
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <ArrowBackIcon fontSize="small" />
          Retour aux niveaux
        </Link>
        <Typography color="text.primary">
          Classes - {selectedLevel?.name}
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {classes.map((classItem, index) => {
          const classColors = [
            'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
            'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
          ];
          const classColor = classColors[index % classColors.length];
          const isDisabled = !selectedSchoolYear || !selectedTrimester;
          
          return (
            <Grid item xs={12} sm={6} md={4} key={classItem.id}>
              <Card 
                sx={{ 
                  cursor: isDisabled ? 'not-allowed' : 'pointer', 
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: isDisabled ? 'none' : 'translateY(-6px) scale(1.02)',
                    boxShadow: isDisabled ? 'none' : '0 12px 30px rgba(0,0,0,0.2)'
                  },
                  background: classColor,
                  borderRadius: 3,
                  opacity: isDisabled ? 0.6 : 1
                }}
                onClick={() => {
                  if (!isDisabled) {
                    fetchStudentsData(classItem, selectedSchoolYear!, selectedTrimester!);
                  }
                }}
              >
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <PeopleIcon 
                    sx={{ 
                      fontSize: 48, 
                      color: 'white', 
                      mb: 2,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                    }} 
                  />
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', mb: 1 }}>
                    {classItem.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)' }}>
                    {isDisabled ? 'Sélectionnez une année et un trimestre' : 'Cliquer pour voir les élèves'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  // Render students
  const renderStudents = () => {
    const studentsArray = Array.isArray(students) ? students : [];
    
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              goBack();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ArrowBackIcon fontSize="small" />
            Retour aux classes
          </Link>
          <Typography color="text.primary">
            Élèves - {selectedClass?.name}
          </Typography>
        </Breadcrumbs>

        <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Liste des élèves
            </Typography>
            <Button
              variant="contained"
              onClick={downloadExcel}
              disabled={studentsArray.length === 0}
              startIcon={<DownloadIcon />}
              sx={{
                backgroundColor: studentsArray.length === 0 
                  ? 'rgba(255,255,255,0.3)' 
                  : '#4caf50',
                color: 'white',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: studentsArray.length === 0 
                    ? 'rgba(255,255,255,0.3)' 
                    : '#388e3c',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              Télécharger Excel
            </Button>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AssessmentIcon sx={{ color: '#1976d2' }} />
            <Chip 
              label={`${studentsArray.length} élève(s) trouvé(s)`}
              color="info"
              sx={{ fontWeight: 600 }}
            />
          </Box>

          {studentsArray.length === 0 ? (
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: 2,
                backgroundColor: '#e3f2fd',
                color: '#1976d2'
              }}
            >
              Aucun élève trouvé pour cette classe.
            </Alert>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>N°</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Prénom</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Matricule</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Moyenne</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rang</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {studentsArray.map((student, index) => (
                    <TableRow 
                      key={student.matricule}
                      sx={{
                        '&:nth-of-type(odd)': {
                          backgroundColor: '#f9f9f9',
                        },
                        '&:hover': {
                          backgroundColor: '#e3f2fd',
                        }
                      }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{student.last_name}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{student.first_name}</TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>{student.matricule}</TableCell>
                      <TableCell>
                        <Chip 
                          label={Number(student.average || 0).toFixed(2)} 
                          color={Number(student.average || 0) >= 10 ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={student.rank} 
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    );
  };

  // Render all students
  const renderAllStudents = () => {
    const studentsArray = Array.isArray(students) ? students : [];
    
    // Récupérer toutes les matières uniques de tous les élèves
    const allSubjects = new Set<string>();
    studentsArray.forEach(student => {
      if (student.subjects && student.subjects.length > 0) {
        student.subjects.forEach(subject => {
          allSubjects.add(subject.subject_name);
        });
      }
    });
    
    const subjectsArray = Array.from(allSubjects).sort();
    
    return (
      <Box>
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => {
              e.preventDefault();
              goBack();
            }}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <ArrowBackIcon fontSize="small" />
            Retour aux niveaux
          </Link>
          <Typography color="text.primary">
            Rapport complet de l'établissement
          </Typography>
        </Breadcrumbs>

        {/* En-tête avec nom de l'établissement */}
        <Paper sx={{ 
          p: 2, 
          mb: 3,
          borderRadius: 2,
          backgroundColor: '#f8f9fa',
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
                RAPPORT COMPLET DE L'ÉTABLISSEMENT - {selectedTrimester?.name}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                GROUPE SCOLAIRE TRAYE BERNARD
              </Typography>
              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                Année scolaire: {selectedSchoolYear?.year || selectedSchoolYear?.name} | 
                Total élèves: {studentsArray.length}
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={downloadExcel}
              disabled={studentsArray.length === 0}
              startIcon={<DownloadIcon />}
              sx={{
                backgroundColor: studentsArray.length === 0 
                  ? 'rgba(255,255,255,0.3)' 
                  : '#4caf50',
                color: 'white',
                fontWeight: 600,
                borderRadius: 2,
                px: 3,
                py: 1,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  backgroundColor: studentsArray.length === 0 
                    ? 'rgba(255,255,255,0.3)' 
                    : '#388e3c',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }
              }}
            >
              Télécharger Excel
            </Button>
          </Box>
        </Paper>

        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            border: '1px solid #e0e0e0',
            overflow: 'auto'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ 
                backgroundColor: 'primary.main',
                '& .MuiTableCell-head': {
                  color: 'white',
                  fontWeight: 700,
                  fontSize: 12,
                  border: '1px solid rgba(255,255,255,0.2)'
                }
              }}>
                <TableCell>Matricule</TableCell>
                <TableCell>Nom</TableCell>
                <TableCell>Prénom</TableCell>
                <TableCell>Nom de l'établissement</TableCell>
                <TableCell>Niveau</TableCell>
                {subjectsArray.map(subject => (
                  <TableCell key={subject} align="center" sx={{ minWidth: 80, fontSize: 10 }}>
                    {subject}
                  </TableCell>
                ))}
                <TableCell align="center">Moyenne Trimestrielle</TableCell>
                <TableCell align="center">Rang</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {studentsArray
                .sort((a, b) => {
                  // Trier par niveau dans l'ordre exact : 6ème, 5ème, 4ème, 3ème, 2nde, 1ère, Terminale
                  const levelOrder = [
                    '6ème', '6eme', '6eme année', '6ème année',
                    '5ème', '5eme', '5eme année', '5ème année', 
                    '4ème', '4eme', '4eme année', '4ème année',
                    '3ème', '3eme', '3eme année', '3ème année',
                    '2nde', '2nde année', 'seconde',
                    '1ère', '1ere', '1ere année', '1ère année', 'première', 'premiere',
                    'Terminale', 'terminale', 'terminale année'
                  ];
                  
                  // Normaliser les noms de niveaux pour la comparaison
                  const normalizeLevel = (level: string) => {
                    if (!level) return '';
                    return level.toLowerCase().trim().replace(/[éèêë]/g, 'e').replace(/[àâä]/g, 'a');
                  };
                  
                  const levelA = normalizeLevel((a as any).level_name || '');
                  const levelB = normalizeLevel((b as any).level_name || '');
                  
                  // Trouver l'index dans l'ordre des niveaux
                  const getLevelIndex = (level: string) => {
                    if (level.includes('6') || level.includes('six')) return 0;
                    if (level.includes('5') || level.includes('cinq')) return 1;
                    if (level.includes('4') || level.includes('quatre')) return 2;
                    if (level.includes('3') || level.includes('trois')) return 3;
                    if (level.includes('2nde') || level.includes('seconde')) return 4;
                    if (level.includes('1') || level.includes('premiere') || level.includes('première')) return 5;
                    if (level.includes('terminale')) return 6;
                    return 999; // Niveau non reconnu
                  };
                  
                  const indexA = getLevelIndex(levelA);
                  const indexB = getLevelIndex(levelB);
                  
                  // D'abord trier par niveau (6ème en premier, Terminale en dernier)
                  if (indexA !== indexB) return indexA - indexB;
                  
                  // Ensuite trier par classe dans le même niveau
                  if ((a as any).class_name !== (b as any).class_name) return ((a as any).class_name || '').localeCompare((b as any).class_name || '');
                  
                  // Enfin trier par nom de famille puis prénom
                  const lastNameA = (a.last_name || '').trim().toLowerCase();
                  const lastNameB = (b.last_name || '').trim().toLowerCase();
                  const firstNameA = (a.first_name || '').trim().toLowerCase();
                  const firstNameB = (b.first_name || '').trim().toLowerCase();
                  
                  if (lastNameA < lastNameB) return -1;
                  if (lastNameA > lastNameB) return 1;
                  if (firstNameA < firstNameB) return -1;
                  if (firstNameA > firstNameB) return 1;
                  return 0;
                })
                .map((student, index) => (
                  <TableRow 
                    key={`${(student as any).level_name}-${(student as any).class_name}-${student.matricule}`}
                    sx={{
                      '&:nth-of-type(odd)': {
                        backgroundColor: '#f5f5f5',
                      },
                      '&:hover': {
                        backgroundColor: '#e3f2fd',
                        transition: 'all 0.2s ease'
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {student.matricule}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {student.last_name}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      {student.first_name}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>
                      GROUPE SCOLAIRE TRAYE BERNARD
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#7b1fa2' }}>
                      {(student as any).level_name}
                    </TableCell>
                    {/* Afficher les moyennes pour chaque matière avec codes spéciaux */}
                    {subjectsArray.map(subjectName => {
                      const subject = student.subjects?.find(s => s.subject_name === subjectName);
                      let displayValue = '-';
                      let cellStyle: any = { fontWeight: 600 };
                      
                      if (subject) {
                        if (subject.rank === null || subject.rank === undefined) {
                          displayValue = '(21)'; // Élève non classé
                          cellStyle = { ...cellStyle, color: '#ff9800', backgroundColor: '#fff3e0' };
                        } else {
                          displayValue = Number(subject.average || 0).toFixed(2);
                          cellStyle = { ...cellStyle, color: '#000' };
                        }
                      } else {
                        displayValue = '(22)'; // Matière pas dans l'emploi du temps
                        cellStyle = { ...cellStyle, color: '#f44336', backgroundColor: '#ffebee' };
                      }
                      
                      return (
                        <TableCell key={subjectName} align="center" sx={cellStyle}>
                          {displayValue}
                        </TableCell>
                      );
                    })}
                    <TableCell align="center">
                      <Chip 
                        label={Number(student.average || 0).toFixed(2)} 
                        color={Number(student.average || 0) >= 10 ? 'success' : 'error'}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          '&.MuiChip-colorSuccess': {
                            backgroundColor: '#4caf50',
                            color: 'white'
                          },
                          '&.MuiChip-colorError': {
                            backgroundColor: '#f44336',
                            color: 'white'
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={student.rank} 
                        color="primary"
                        size="small"
                        sx={{
                          fontWeight: 700,
                          backgroundColor: '#2196f3',
                          color: 'white'
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <SecretarySidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Paper sx={{ 
          p: 4, 
          borderRadius: 2,
          backgroundColor: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          border: '1px solid #e0e0e0'
        }}>
          {/* En-tête */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2', mb: 1 }}>
              {exportType === 'moyennes' ? 'Export des Moyennes' : 'Récapitulatif des Élèves'}
            </Typography>
            <Typography variant="body1" sx={{ color: '#666' }}>
              {exportType === 'moyennes' 
                ? 'Générez des rapports détaillés des moyennes des élèves' 
                : 'Consultez le récapitulatif des élèves par classe'
              }
            </Typography>
          </Box>

          {/* Messages d'erreur */}
          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {/* Loading */}
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Contenu principal */}
          {!loading && currentView === 'levels' && renderLevels()}
          {!loading && currentView === 'classes' && renderClasses()}
          {!loading && currentView === 'students' && renderStudents()}
          {!loading && currentView === 'allStudents' && renderAllStudents()}
        </Paper>
      </Box>
    </Box>
  );
};

export default ExcelFiles;